import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { SearchInput } from "@src/components/ui-components/Inputs/SearchInput/SearchInput";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { LocalData } from "to-be-removed/LocalData/General/LocalData";
import { MemberData } from "to-be-removed/LocalData/Property/MemberData";
import { UserData } from "../../../to-be-removed/LocalData/User/UserData";
import { Spacer } from "../../ui-components/Spacer/Spacer";
import { ModalPopup } from "../ModalPopup/ModalPopup";
import { Text } from "../Text/Text";
import { UserImageView } from "../UserImageView/UserImageView";
import styles from "./UserSelectPopUp.module.css";

interface UserSelectPopupProps {
  id: string;
  didSelectUsers: (users: UserData[]) => void;
  title?: string;
  options?: UserData[];
}

export const UserSelectPopup = (props: UserSelectPopupProps) => {
  const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);
  const [userOptions, setUserOptions] = useState<UserData[]>(props.options ?? []);
  const [searchText, setSearchText] = useState<string>("");

  const displayedUsers = useMemo(() => {
    return userOptions.filter((user) => user.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [userOptions, searchText]);

  useEffect(() => {
    if (props.options) {
      setUserOptions(props.options);
    } else {
      loadPropertyMembersAsUsers().then((response) => {
        let loadedUserOptions = [...response].filter((user) => !user.deleted).sort((a, b) => a.name.localeCompare(b.name));
        setUserOptions(loadedUserOptions);
      });
    }
  }, [props]);

  return (
    <ModalPopup
      id={props.id}
      onActionClicked={() => props.didSelectUsers(selectedUsers)}
      title={props.title ?? t("main.selectUsers")}
      children={
        <div className={styles.container}>
          <SearchInput onChange={setSearchText} value={searchText} />
          <div className={styles.usersList}>
            {displayedUsers.map((user, index) => {
              return (
                <div className={styles.userCell} key={user.id}>
                  <UserImageView user={user} size={24} fontSize={14} />
                  <Text marginLeft={8} size="sm">
                    {user.name}
                  </Text>
                  <Spacer />
                  <Checkbox
                    data-cy={`user-option-${index}`}
                    value={selectedUsers.includes(user)}
                    onChange={(check) => {
                      if (check) setSelectedUsers([...selectedUsers, user]);
                      else setSelectedUsers(selectedUsers.filter((u) => u !== user));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      }
    />
  );
};

export function loadPropertyMembersAsUsers(): Promise<UserData[]> {
  let users: UserData[] = [];
  const properties = LocalData.currentUser.properties;
  let promise: Promise<UserData[]> = new Promise((resolve, reject) => {
    let loadedPMs = 0;
    let totalMs = 0;
    let loadedMs = 0;
    properties.forEach((prop) => {
      if (prop.members.loaded) {
        loadedPMs++;
        loadedPropertyMembers(prop.members.documents);
      } else {
        prop.members.load((loadedMembers) => {
          loadedPMs++;
          loadedPropertyMembers(loadedMembers);
        });
      }
      function loadedPropertyMembers(loadedMembers: MemberData[]) {
        totalMs += loadedMembers.length;
        loadedMembers.forEach((mem) => {
          if (mem.user.loaded) {
            loadedMs++;
            if (!users.includes(mem.user)) {
              users.push(mem.user);
            }
            //Resolve the promise on the last member of the last property
            if (loadedPMs === properties.length && loadedMs === totalMs) {
              resolve(users.sort((a, b) => a.name.localeCompare(b.name)));
            }
          } else {
            mem.user.loadWithCompletionHandler((err) => {
              loadedMs++;
              if (!users.includes(mem.user)) {
                users.push(mem.user);
              }
              //Resolve the promise on the last member of the last property
              if (loadedPMs === properties.length && loadedMs === totalMs) {
                resolve(users.sort((a, b) => a.name.localeCompare(b.name)));
              }
            });
          }
        });
      }
    });
  });
  return promise;
}
